import api from '@/lib/axios';
import { downloadExport, ExportFormat } from '@/services/school.api';
import type { StudentPortfolio } from '@/services/reports.api';
import type { CreateLessonFileData, LessonFile } from '@/services/academic.api';
import type { DataTableParams, PaginatedResult } from '@/components/data-table';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export interface OrgStudentReportRow {
  schoolId: string;
  schoolName: string;
  studentId: string;
  admissionNo: string;
  studentName: string;
  className: string;
  sectionName: string;
  dob: string | null;
  gender: string | null;
  username: string | null;
  photo: string | null;
  rollNumber: number | null;
  admissionDate: string;
}

export interface ListOrgStudentsReportParams extends Partial<DataTableParams> {
  schoolId?: string;
  classId?: string;
  sectionId?: string;
}

export interface OrgDepartmentRow {
  schoolId: string;
  schoolName: string;
  departmentId: string;
  departmentName: string;
  description: string | null;
  subjectIds: string[];
  subjectNames: string[];
  autoGenerated: boolean;
  teacherCount: number;
  teacherNames: string[];
}

export interface ListOrgDepartmentsParams {
  schoolId?: string;
}

export interface OrgLessonRow {
  schoolId: string;
  schoolName: string;
  lessonId: string;
  lessonName: string;
  description: string | null;
  className: string;
  sectionName: string;
  subjectName: string;
  fileCount: number;
  topicCount: number;
  createdAt: string;
}

export interface ListOrgLessonsParams extends Partial<DataTableParams> {
  schoolId?: string;
  classId?: string;
  classSubjectId?: string;
}

export interface OrgLessonTopicRow {
  schoolId: string;
  schoolName: string;
  topicId: string;
  topicName: string;
  description: string | null;
  lessonId: string;
  lessonName: string;
  className: string;
  sectionName: string;
  subjectName: string;
  fileCount: number;
  createdAt: string;
}

export interface ListOrgLessonTopicsParams extends ListOrgLessonsParams {
  lessonId?: string;
}

/**
 * Unmodified passthrough of the school-tier LessonDetailDto (org
 * lessonDetail() calls LessonsService.findOne() as-is — reuse verbatim,
 * not remapped to the report row's field names). Field names deliberately
 * differ from OrgLessonRow (`id`/`name` here vs `lessonId`/`lessonName`
 * there, no `schoolId`/`schoolName` at all) — callers that need the
 * school context already have it from the row they opened this from.
 */
export interface OrgLessonDetail {
  id: string;
  sectionId: string;
  sectionName: string;
  classId: string;
  className: string;
  classSubjectId: string;
  subjectName: string;
  name: string;
  description: string | null;
  createdByName: string | null;
  fileCount: number;
  topicCount: number;
  createdAt: string;
  updatedAt: string;
  files: LessonFile[];
}

/** Same passthrough shape, one level down (school-tier LessonTopicDetailDto). */
export interface OrgLessonTopicDetail {
  id: string;
  lessonId: string;
  sectionId: string;
  sectionName: string;
  className: string;
  classSubjectId: string;
  subjectName: string;
  name: string;
  description: string | null;
  fileCount: number;
  createdAt: string;
  updatedAt: string;
  files: LessonFile[];
}

export interface CreateOrgLessonData {
  schoolId: string;
  sectionId: string;
  classSubjectId: string;
  name: string;
  description?: string;
}

export interface UpdateOrgLessonData {
  sectionId?: string;
  classSubjectId?: string;
  name?: string;
  description?: string;
}

export interface CreateOrgLessonTopicData {
  schoolId: string;
  lessonId: string;
  sectionId: string;
  classSubjectId: string;
  name: string;
  description?: string;
}

export interface UpdateOrgLessonTopicData {
  sectionId?: string;
  classSubjectId?: string;
  name?: string;
  description?: string;
}

export interface OrgClassSubjectOption {
  classSubjectId: string;
  subjectName: string;
}

export interface OrgAcademicCalendarRow {
  schoolId: string;
  schoolName: string;
  id: string;
  name: string;
  type: string;
  color: string;
  term: string | null;
  date: string;
  academicYearId: string | null;
  academicYearName: string | null;
}

export interface ListOrgAcademicCalendarParams {
  schoolId?: string;
  academicYearId?: string;
  term?: string;
  month?: number;
  type?: string;
}

export interface DataExportParams {
  schoolId?: string;
}

export interface OrgFinanceReportRow {
  schoolId: string;
  schoolName: string;
  academicYearId: string | null;
  academicYearName: string | null;
  month: number;
  totalIncome: number;
  totalExpense: number;
  balance: number;
}

export interface ListOrgFinanceReportParams {
  schoolId?: string;
  academicYearId?: string;
}

export type OrgLoginReportView = 'never' | 'last';

export interface OrgLoginReportRow {
  schoolId: string;
  schoolName: string;
  userId: string;
  name: string;
  email: string | null;
  role: string;
  lastLoginAt: string | null;
  lastLoginType: string | null;
}

export interface ListOrgLoginReportParams {
  view?: OrgLoginReportView;
  role?: string;
  schoolId?: string;
}

export interface OrgTeachersReportRow {
  schoolId: string;
  schoolName: string;
  employeeId: string;
  staffName: string;
  gender: string | null;
  email: string | null;
  designation: string | null;
  department: string | null;
  salary: number;
  address: string | null;
  status: string;
}

export interface ListOrgTeachersReportParams {
  schoolId?: string;
  showDeactive?: boolean;
}

export interface OrgStudentsAttendanceSummaryRow {
  schoolId: string;
  schoolName: string;
  classes: { className: string; total: number; present: number; absent: number }[];
}

export interface ListOrgStudentsAttendanceSummaryParams {
  date?: string;
  schoolId?: string;
  stateId?: number;
  status?: 'active' | 'inactive' | 'all';
  attendanceType?: '1' | '0';
  showDeleted?: boolean;
  className?: string;
  search?: string;
}

export interface OrgTeachersAttendanceSummaryRow {
  schoolId: string;
  schoolName: string;
  totalTeachers: number;
  totalPresent: number;
  totalAbsent: number;
}

export interface ListOrgTeachersAttendanceSummaryParams {
  date?: string;
  startDate?: string;
  endDate?: string;
  schoolId?: string;
  stateId?: number;
  status?: 'active' | 'inactive' | 'all';
  role?: string;
  showDeleted?: boolean;
  search?: string;
}

export interface OrgStudentAttendanceReportRow {
  schoolId: string;
  schoolName: string;
  studentId: string;
  studentName: string;
  className: string;
  workingDays: number;
  totalPresent: number;
  totalAbsent: number;
  percentage: number;
}

export interface ListOrgStudentAttendanceReportParams extends Partial<DataTableParams> {
  schoolId?: string;
  classId?: string;
  startDate?: string;
  endDate?: string;
}

export interface OrgTeacherAttendanceReportRow {
  schoolId: string;
  schoolName: string;
  teacherId: string;
  teacherName: string;
  workingDays: number;
  totalPresent: number;
  totalAbsent: number;
  percentage: number;
}

export interface ListOrgTeacherAttendanceReportParams extends Partial<DataTableParams> {
  schoolId?: string;
  startDate?: string;
  endDate?: string;
  stateId?: number;
}

export interface OrgDetailedStudentAttendanceRow {
  schoolId: string;
  schoolName: string;
  studentId: string;
  studentName: string;
  className: string;
  totalPresent: number;
  totalAbsent: number;
  cells: Record<string, 'P' | 'A' | '-'>;
}

export interface ListOrgDetailedStudentAttendanceParams extends Partial<DataTableParams> {
  schoolId?: string;
  classId?: string;
  startDate?: string;
  endDate?: string;
  stateId?: number;
}

export interface OrgDetailedTeacherAttendanceRow {
  schoolId: string;
  schoolName: string;
  teacherId: string;
  teacherName: string;
  totalPresent: number;
  totalAbsent: number;
  cells: Record<string, string>;
}

export interface ListOrgDetailedTeacherAttendanceParams extends Partial<DataTableParams> {
  schoolId?: string;
  startDate?: string;
  endDate?: string;
  stateId?: number;
}

export interface OrgStudentSummaryReportRow {
  schoolId: string;
  schoolName: string;
  cells: Record<string, number>;
  total: number;
}

export interface OrgStudentSummaryReportResult {
  rows: OrgStudentSummaryReportRow[];
  classNames: string[];
  classTotals: Record<string, number>;
  grandTotal: number;
}

export interface ListOrgStudentSummaryReportParams {
  schoolId?: string;
  stateId?: number;
  status?: 'active' | 'inactive' | 'all';
  showDeleted?: boolean;
  search?: string;
}

export interface OrgParentAttendanceCenterWiseCell {
  total: number;
  present: number;
  absent: number;
}

export interface OrgParentAttendanceCenterWiseRow {
  schoolId: string;
  schoolName: string;
  cells: Record<string, OrgParentAttendanceCenterWiseCell>;
}

export interface OrgParentAttendanceCenterWiseResult {
  rows: OrgParentAttendanceCenterWiseRow[];
  classNames: string[];
}

export interface ListOrgParentAttendanceCenterWiseParams {
  schoolId?: string;
  startDate?: string;
  endDate?: string;
  stateId?: number;
}

export interface OrgSubjectTeacherCountRow {
  schoolId: string;
  schoolName: string;
  subjects: { subjectId: string; subjectName: string; teacherCount: number }[];
  teachers: { teacherId: string; teacherName: string; subjects: string[] }[];
}

export interface ListOrgSubjectTeacherCountParams {
  schoolId?: string;
  stateId?: number;
}

export interface OrgStateOption {
  id: number;
  name: string;
}

export interface OrgCourseRegistrationRow {
  id: string;
  name: string;
  location: string;
  phone: string;
  gender: string | null;
  course: string;
  createdAt: string;
}

export interface ListOrgCourseRegistrationsParams extends Partial<DataTableParams> {
  gender?: 'Male' | 'Female';
  course?: string;
}

export interface OrgVideoViewReportRow {
  id: string;
  schoolId: string;
  schoolName: string;
  subjectId: string;
  subjectName: string;
  lessonName: string;
  topicName: string | null;
  videoName: string;
  totalViews: number;
  totalDuration: string | null;
  watchedDuration: string | null;
  watchedPercentage: number | null;
  lastWatched: string | null;
  applicableStudents: number;
}

export interface ListOrgVideoViewReportParams {
  schoolId?: string;
  classId?: string;
  subjectId?: string;
}

export interface OrgAffidavitRow {
  id: string;
  schoolId: string;
  schoolName: string;
  className: string;
  studentName: string;
  guardianName: string | null;
  studentSignatureUrl: string | null;
  guardianSignatureUrl: string | null;
  createdAt: string;
}

export interface ListOrgAffidavitsParams {
  schoolId?: string;
  classSectionId?: string;
  studentId?: string;
}

export interface OrgAffidavitStudentOption {
  id: string;
  name: string;
}

export interface OrgUsageLogRow {
  schoolId: string;
  schoolName: string;
  routePattern: string;
  method: string;
  hits: number;
  avgDurationMs: number;
  lastHitAt: string | null;
}

export interface ListOrgUsageLogsParams {
  schoolId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export const OrgReportsApi = {
  exportDataExportStudents: (format: ExportFormat, params?: DataExportParams) =>
    downloadExport('/org/reports/data-export/students/export', 'students-roster', format, params as Record<string, unknown>),

  exportDataExportStaff: (format: ExportFormat, params?: DataExportParams) =>
    downloadExport('/org/reports/data-export/staff/export', 'staff-roster', format, params as Record<string, unknown>),


  students: async (
    params?: ListOrgStudentsReportParams,
  ): Promise<PaginatedResult<OrgStudentReportRow> & { truncated: boolean }> =>
    unwrap(await api.get('/org/reports/students', { params })),

  exportStudents: (format: ExportFormat, params?: Pick<ListOrgStudentsReportParams, 'schoolId' | 'search' | 'classId' | 'sectionId'>) =>
    downloadExport('/org/reports/students/export', 'students-report', format, params as Record<string, unknown>),

  departments: async (params?: ListOrgDepartmentsParams): Promise<OrgDepartmentRow[]> =>
    unwrap(await api.get('/org/reports/departments', { params })),

  lessons: async (params?: ListOrgLessonsParams): Promise<PaginatedResult<OrgLessonRow> & { truncated: boolean }> =>
    unwrap(await api.get('/org/reports/lessons', { params })),

  exportLessons: (format: ExportFormat, params?: Pick<ListOrgLessonsParams, 'schoolId' | 'search' | 'classId' | 'classSubjectId'>) =>
    downloadExport('/org/reports/lessons/export', 'lessons-report', format, params as Record<string, unknown>),

  lessonTopics: async (
    params?: ListOrgLessonTopicsParams,
  ): Promise<PaginatedResult<OrgLessonTopicRow> & { truncated: boolean }> =>
    unwrap(await api.get('/org/reports/lesson-topics', { params })),

  exportLessonTopics: (format: ExportFormat, params?: ListOrgLessonTopicsParams) =>
    downloadExport('/org/reports/lesson-topics/export', 'lesson-topics-report', format, params as Record<string, unknown>),

  classSubjectsForClass: async (schoolId: string, classId: string): Promise<OrgClassSubjectOption[]> =>
    unwrap(await api.get('/org/reports/lessons/class-subjects', { params: { schoolId, classId } })),

  lessonDetail: async (schoolId: string, lessonId: string): Promise<OrgLessonDetail> =>
    unwrap(await api.get(`/org/reports/lessons/${schoolId}/${lessonId}`)),

  createLesson: async (data: CreateOrgLessonData): Promise<OrgLessonRow> =>
    unwrap(await api.post('/org/reports/lessons', data)),

  updateLesson: async (schoolId: string, lessonId: string, data: UpdateOrgLessonData): Promise<OrgLessonRow> =>
    unwrap(await api.patch(`/org/reports/lessons/${schoolId}/${lessonId}`, data)),

  removeLesson: async (schoolId: string, lessonId: string): Promise<void> =>
    unwrap(await api.delete(`/org/reports/lessons/${schoolId}/${lessonId}`)),

  addLessonFile: async (schoolId: string, lessonId: string, data: CreateLessonFileData): Promise<LessonFile> =>
    unwrap(await api.post(`/org/reports/lessons/${schoolId}/${lessonId}/files`, data)),

  removeLessonFile: async (schoolId: string, lessonId: string, fileId: string): Promise<void> =>
    unwrap(await api.delete(`/org/reports/lessons/${schoolId}/${lessonId}/files/${fileId}`)),

  lessonTopicDetail: async (schoolId: string, topicId: string): Promise<OrgLessonTopicDetail> =>
    unwrap(await api.get(`/org/reports/lesson-topics/${schoolId}/${topicId}`)),

  createLessonTopic: async (data: CreateOrgLessonTopicData): Promise<OrgLessonTopicRow> =>
    unwrap(await api.post('/org/reports/lesson-topics', data)),

  updateLessonTopic: async (schoolId: string, topicId: string, data: UpdateOrgLessonTopicData): Promise<OrgLessonTopicRow> =>
    unwrap(await api.patch(`/org/reports/lesson-topics/${schoolId}/${topicId}`, data)),

  removeLessonTopic: async (schoolId: string, topicId: string): Promise<void> =>
    unwrap(await api.delete(`/org/reports/lesson-topics/${schoolId}/${topicId}`)),

  addLessonTopicFile: async (schoolId: string, topicId: string, data: CreateLessonFileData): Promise<LessonFile> =>
    unwrap(await api.post(`/org/reports/lesson-topics/${schoolId}/${topicId}/files`, data)),

  removeLessonTopicFile: async (schoolId: string, topicId: string, fileId: string): Promise<void> =>
    unwrap(await api.delete(`/org/reports/lesson-topics/${schoolId}/${topicId}/files/${fileId}`)),

  academicCalendar: async (params?: ListOrgAcademicCalendarParams): Promise<OrgAcademicCalendarRow[]> =>
    unwrap(await api.get('/org/reports/academic-calendar', { params })),

  exportAcademicCalendar: (format: ExportFormat, params?: ListOrgAcademicCalendarParams) =>
    downloadExport('/org/reports/academic-calendar/export', 'academic-calendar-report', format, params as Record<string, unknown>),

  financeReport: async (params?: ListOrgFinanceReportParams): Promise<OrgFinanceReportRow[]> =>
    unwrap(await api.get('/org/reports/finance', { params })),

  exportFinanceReport: (format: ExportFormat, params?: ListOrgFinanceReportParams) =>
    downloadExport('/org/reports/finance/export', 'finance-report', format, params as Record<string, unknown>),

  loginReport: async (params?: ListOrgLoginReportParams): Promise<OrgLoginReportRow[]> =>
    unwrap(await api.get('/org/reports/login', { params })),

  exportLoginReport: (format: ExportFormat, params?: ListOrgLoginReportParams) =>
    downloadExport('/org/reports/login/export', 'login-report', format, params as Record<string, unknown>),

  teachersReport: async (params?: ListOrgTeachersReportParams): Promise<OrgTeachersReportRow[]> =>
    unwrap(await api.get('/org/reports/teachers', { params })),

  exportTeachersReport: (format: ExportFormat, params?: ListOrgTeachersReportParams) =>
    downloadExport('/org/reports/teachers/export', 'teachers-report', format, params as Record<string, unknown>),

  studentPortfolio: async (schoolId: string, studentId: string): Promise<StudentPortfolio> =>
    unwrap(await api.get(`/org/reports/students/${schoolId}/${studentId}/portfolio`)),

  studentsAttendanceSummary: async (params?: ListOrgStudentsAttendanceSummaryParams): Promise<OrgStudentsAttendanceSummaryRow[]> =>
    unwrap(await api.get('/org/reports/students-attendance-summary', { params })),

  exportStudentsAttendanceSummary: (format: ExportFormat, params?: ListOrgStudentsAttendanceSummaryParams) =>
    downloadExport('/org/reports/students-attendance-summary/export', 'students-attendance-summary', format, params as Record<string, unknown>),

  teachersAttendanceSummary: async (
    params?: ListOrgTeachersAttendanceSummaryParams,
  ): Promise<OrgTeachersAttendanceSummaryRow[]> => unwrap(await api.get('/org/reports/teachers-attendance-summary', { params })),

  exportTeachersAttendanceSummary: (format: ExportFormat, params?: ListOrgTeachersAttendanceSummaryParams) =>
    downloadExport('/org/reports/teachers-attendance-summary/export', 'teachers-attendance-summary', format, params as Record<string, unknown>),

  studentAttendanceReport: async (
    params?: ListOrgStudentAttendanceReportParams,
  ): Promise<PaginatedResult<OrgStudentAttendanceReportRow> & { truncated: boolean }> =>
    unwrap(await api.get('/org/reports/student-attendance-report', { params })),

  exportStudentAttendanceReport: (format: ExportFormat, params?: ListOrgStudentAttendanceReportParams) =>
    downloadExport('/org/reports/student-attendance-report/export', 'student-attendance-report', format, params as Record<string, unknown>),

  teacherAttendanceReport: async (
    params?: ListOrgTeacherAttendanceReportParams,
  ): Promise<PaginatedResult<OrgTeacherAttendanceReportRow> & { truncated: boolean }> =>
    unwrap(await api.get('/org/reports/teacher-attendance-report', { params })),

  exportTeacherAttendanceReport: (format: ExportFormat, params?: ListOrgTeacherAttendanceReportParams) =>
    downloadExport('/org/reports/teacher-attendance-report/export', 'teacher-attendance-report', format, params as Record<string, unknown>),

  detailedStudentAttendance: async (
    params?: ListOrgDetailedStudentAttendanceParams,
  ): Promise<PaginatedResult<OrgDetailedStudentAttendanceRow> & { truncated: boolean; dates: string[] }> =>
    unwrap(await api.get('/org/reports/detailed-student-attendance', { params })),

  exportDetailedStudentAttendance: (format: ExportFormat, params?: ListOrgDetailedStudentAttendanceParams) =>
    downloadExport('/org/reports/detailed-student-attendance/export', 'detailed-student-attendance', format, params as Record<string, unknown>),

  detailedTeacherAttendance: async (
    params?: ListOrgDetailedTeacherAttendanceParams,
  ): Promise<PaginatedResult<OrgDetailedTeacherAttendanceRow> & { truncated: boolean; dates: string[] }> =>
    unwrap(await api.get('/org/reports/detailed-teacher-attendance', { params })),

  exportDetailedTeacherAttendance: (format: ExportFormat, params?: ListOrgDetailedTeacherAttendanceParams) =>
    downloadExport('/org/reports/detailed-teacher-attendance/export', 'detailed-teacher-attendance', format, params as Record<string, unknown>),

  studentSummaryReport: async (params?: ListOrgStudentSummaryReportParams): Promise<OrgStudentSummaryReportResult> =>
    unwrap(await api.get('/org/reports/student-summary', { params })),

  exportStudentSummaryReport: (format: ExportFormat, params?: ListOrgStudentSummaryReportParams) =>
    downloadExport('/org/reports/student-summary/export', 'student-summary-report', format, params as Record<string, unknown>),

  parentAttendanceCenterWise: async (params?: ListOrgParentAttendanceCenterWiseParams): Promise<OrgParentAttendanceCenterWiseResult> =>
    unwrap(await api.get('/org/reports/parent-attendance-center-wise', { params })),

  exportParentAttendanceCenterWise: (format: ExportFormat, params?: ListOrgParentAttendanceCenterWiseParams) =>
    downloadExport('/org/reports/parent-attendance-center-wise/export', 'parent-attendance-center-wise', format, params as Record<string, unknown>),

  subjectTeacherCount: async (params?: ListOrgSubjectTeacherCountParams): Promise<OrgSubjectTeacherCountRow[]> =>
    unwrap(await api.get('/org/reports/subject-teacher-count', { params })),

  exportSubjectTeacherCount: (format: ExportFormat, params?: ListOrgSubjectTeacherCountParams) =>
    downloadExport('/org/reports/subject-teacher-count/export', 'subject-teacher-count-report', format, params as Record<string, unknown>),

  listStates: async (): Promise<OrgStateOption[]> => unwrap(await api.get('/org/reports/states')),

  courseRegistrations: async (params?: ListOrgCourseRegistrationsParams): Promise<PaginatedResult<OrgCourseRegistrationRow>> =>
    unwrap(await api.get('/org/reports/course-registrations', { params })),

  videoViewReport: async (params?: ListOrgVideoViewReportParams): Promise<OrgVideoViewReportRow[]> =>
    unwrap(await api.get('/org/reports/video-view', { params })),

  exportVideoViewReport: (format: ExportFormat, params?: ListOrgVideoViewReportParams) =>
    downloadExport('/org/reports/video-view/export', 'video-view-report', format, params as Record<string, unknown>),

  affidavits: async (params?: ListOrgAffidavitsParams): Promise<OrgAffidavitRow[]> =>
    unwrap(await api.get('/org/reports/affidavits', { params })),

  exportAffidavits: (format: ExportFormat, params?: ListOrgAffidavitsParams) =>
    downloadExport('/org/reports/affidavits/export', 'affidavits-report', format, params as Record<string, unknown>),

  affidavitStudentsBySection: async (schoolId: string, sectionId: string): Promise<OrgAffidavitStudentOption[]> =>
    unwrap(await api.get('/org/reports/affidavits/students-by-section', { params: { schoolId, sectionId } })),

  usageLogs: async (params?: ListOrgUsageLogsParams): Promise<OrgUsageLogRow[]> =>
    unwrap(await api.get('/org/reports/usage-logs', { params })),

  exportUsageLogs: (format: ExportFormat, params?: ListOrgUsageLogsParams) =>
    downloadExport('/org/reports/usage-logs/export', 'usage-logs-report', format, params as Record<string, unknown>),
};
